import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  Typography,
  Divider,
  Row,
  Col,
  Breadcrumb,
  message,
  Modal,
  Dropdown,
} from "antd";
import {
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  CarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { FaHeart, FaPlus } from "react-icons/fa";
import { TbDotsVertical } from "react-icons/tb";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useApiCall } from "../services/api";
import { useGlobalModal } from "../context/SuccessModalProvider";
import { useDispatch, useSelector } from "react-redux";
import { disclaimerItems, productsMenuItems, roleIds } from "../constant";
import FileUploadModal from "../components/models/FileUploadModal";
import { triggerRefresh } from "../redux/auth/cartRefreshSlice";
import PrimaryButton from "../components/Forms/PrimaryButton";
import { DropdownMenuItems } from "./Products";
import CustomBadge from "../components/Charts/CustomBadge";
import { setCounts } from "../redux/auth/navCountSlice";
import { handleRouteNav, inchesToCm } from "../utils";
import ProductImageName from "../components/Layout/ProductImageName";
import { PiHeartFill } from "react-icons/pi";

const { Title, Text } = Typography;
const { TextArea } = Input;

const DYED_TYPES = {
  piece: "PIECE DYED",
  pieceLabel: "Piece Dyed",
  top: "TOP DYED",
  topLabel: "Top Dyed",
};

// Utility: safe number
const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const ProductDetails = () => {
  const { postApi, loading } = useApiCall();
  const dispatch = useDispatch();
  const { sort_no } = useParams();
  const { showPopup } = useGlobalModal();
  const { user_id, full_name, role_id, is_customer, customer_id } = useSelector(
    (state) => state.auth,
  );
  const location = useLocation();
  const {
    cart_id,
    shade_no,
    order_cases_no,
    order_item_dyed_type,
    comments: itemComments,
  } = location.state || {};
  // const query = new URLSearchParams(useLocation().search);
  // const shade_no = query.get("shade_no");

  const [orderSplitModal, setOrderSplitModal] = useState(false);
  const [shadesModal, setShadesModal] = useState(false);
  const [modalData, setModalData] = useState({});

  // Data
  const [productData, setProductData] = useState({}); // itemDetails
  const [itemsList, setItemsList] = useState([]); // QualityShade[]
  const [filteredItemsList, setFilteredItemsList] = useState([]);
  const [pdItemsList, setPdItemsList] = useState([]);
  const [tdItemsList, setTdItemsList] = useState([]);

  const [isPDSelected, setIsPDSelected] = useState(
    order_item_dyed_type ? order_item_dyed_type === DYED_TYPES.piece : true,
  ); // _isPDSelected
  const [selectedShadeIndex, setSelectedShadeIndex] = useState(0);

  const [noOfCases, setNoOfCases] = useState(2); // qtyController
  const [comments, setComments] = useState(itemComments || "");
  const [totalQuantity, setTotalQuantity] = useState(0); // _totalQuantity
  const [totalInStockQuantity, setTotalInStockQuantity] = useState(0);
  const [possibleInStockQuantity, setPossibleInStockQuantity] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [cartItem, setCartItem] = useState({});
  const { collapsed } = useSelector((state) => state.navCounts);

  // additional states
  const [currentItem, setCurrentItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [prefillShadeNo, setPrefillShadeNo] = useState(shade_no);

  // Derived
  const selectedType = isPDSelected ? "piece" : "top";
  const navigate = useNavigate();

  // very important function
  const clampAndNormalizeCases = (n, tempIsPDSelected) => {
    let val = Math.max(0, toNumber(n, 0));
    if (tempIsPDSelected ?? isPDSelected) {
      // Min 2, even numbers only
      val = Math.max(2, val);
      if (val % 2 === 1) val = val + 1;
    } else {
      // Min 1
      val = Math.max(1, val);
    }
    return val;
  };

  const handleCheckout = () => {
    navigate(handleRouteNav("/product-catalog/checkout", role_id));
  };

  const fetchProductData = async () => {
    try {
      const response = await postApi(
        is_customer
          ? "product/product-detail"
          : "sales/product/product-details",
        {
          sort_no,
          customer_id: is_customer ? customer_id : undefined,
        },
      );
      if (response.status) {
        setProductData(response.data);
      } else {
        navigate(handleRouteNav("/product-catalog", role_id));
      }
    } catch (error) {
      console.error("Error fetching product data:", error);
    }
  };
  useEffect(() => {
    fetchProductData();
  }, [sort_no]);

  useEffect(() => {
    const fetchCartItem = async () => {
      if (!cart_id) return; // only fetch if editing

      try {
        const res = await postApi(
          is_customer ? "cart/get-single-item" : "sales/cart/get-single-item",
          { cart_id },
        );
        if (res?.status && res?.data) {
          const item = res.data;
          setCartItem(item);

          // Pre-fill product + shade + qty
          setNoOfCases(item.cases || 0);
          // setTotalQuantity(toNumber(item.total_quantity, 0));
          // setTotalInStockQuantity(toNumber(item.total_in_stock_quantity, 0));
          if (item.comments) setComments(item.comments);

          setIsPDSelected(item.dye_type !== DYED_TYPES.top);
          setPrefillShadeNo(item.shade_name);
        } else {
          navigate(handleRouteNav("/product-catalog", role_id));
        }
      } catch (err) {
        console.error("Failed to fetch cart item:", err);
      }
    };

    fetchCartItem();
  }, [cart_id]);

  useEffect(() => {
    if (prefillShadeNo && filteredItemsList.length > 0) {
      const idx = filteredItemsList.findIndex(
        (e) => String(e?.shade?.descr) === String(prefillShadeNo),
      );

      if (idx >= 0) {
        setSelectedShadeIndex(idx);
        setPrefillShadeNo(null); // clear so it won’t run again
      }
    }
  }, [prefillShadeNo, filteredItemsList]);

  // Fetch shades + stock cases (QualityShade list)
  const getShadeStockCases = async () => {
    try {
      const res = await postApi("product/get-shade-stock-cases", { sort_no });
      if (res?.status && Array.isArray(res.data)) {
        const list = res.data.filter(
          (e) => e?.shade && e?.shade?.detail != null,
        );
        // const list = res.data;
        setItemsList(list);

        let tempPdSelected = isPDSelected;
        if (!cart_id && !shade_no) {
          tempPdSelected = list[0]?.dyed_type === DYED_TYPES.piece;
        } else if (shade_no) {
          const item = list.find(
            (e) => String(e?.shade?.descr) === String(shade_no),
          );
          tempPdSelected = order_item_dyed_type === DYED_TYPES.piece;
        } else {
          tempPdSelected = cartItem?.dye_type !== DYED_TYPES.top;
        }

        onPdTdTap(tempPdSelected, list);
        setPdItemsList(list.filter((e) => e?.dyed_type === DYED_TYPES.piece));
        setTdItemsList(list.filter((e) => e?.dyed_type === DYED_TYPES.top));
      } else {
        setItemsList([]);
        setFilteredItemsList([]);
      }
    } catch (e) {
      console.error(e);
      setItemsList([]);
      setFilteredItemsList([]);
    }
  };

  useEffect(() => {
    if (productData) {
      getShadeStockCases();
    }
  }, [productData]);

  const fetchCart = async () => {
    try {
      const payload = {
        customer_id: customer_id,
      };
      const res = await postApi(
        is_customer
          ? "cart/get-cart-customer-id"
          : "sales/cart/get-cart-user-id",
        is_customer ? payload : { user_id },
      );
      if (res?.status && Array.isArray(res.data)) {
        setCartItems(res.data);
        dispatch(setCounts({ cartItems: res?.total_items || 0 }));
      }
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    }
  };
  useEffect(() => {
    fetchCart();
  }, [user_id]);

  // Handle PD/TD toggle + shade preselection + qty defaults
  const onPdTdTap = (value, sourceList = itemsList) => {
    const filtered = (sourceList || []).filter(
      (e) =>
        e?.shade &&
        String(e.dyed_type) === (value ? DYED_TYPES.piece : DYED_TYPES.top),
    );

    if (filtered.length === 0) {
      return null;
    }

    setIsPDSelected(value);
    setFilteredItemsList(filtered);

    if (!cart_id && !shade_no) {
      let nextSelectedIndex = 0;
      let nextNoOfCases = value ? 2 : 1;
      if (filtered.length === 0) {
        nextNoOfCases = 0;
      }
      setSelectedShadeIndex(nextSelectedIndex);
      setNoOfCases(nextNoOfCases);
    } else if (shade_no) {
      const idx = filtered.findIndex(
        (e) => String(e?.shade?.descr) === String(shade_no),
      );
      if (idx >= 0) {
        setSelectedShadeIndex(idx);
      } else {
        setSelectedShadeIndex(0);
      }
      const foundShade = filtered.find(
        (e) => String(e?.shade?.descr) === String(shade_no),
      );
      if (
        (value === true && order_item_dyed_type === DYED_TYPES.piece) ||
        (value === false && order_item_dyed_type === DYED_TYPES.top)
      ) {
        const idx = filtered.findIndex(
          (e) => String(e?.shade?.descr) === String(shade_no),
        );
        if (idx >= 0) {
          setSelectedShadeIndex(idx);
        }

        setNoOfCases(
          order_cases_no
            ? clampAndNormalizeCases(order_cases_no, value)
            : value
              ? 2
              : 1,
        );
      } else {
        setSelectedShadeIndex(0);
        let nextNoOfCases = value ? 2 : 1; // PD->2, TD->1
        if (filtered.length === 0) {
          nextNoOfCases = 0;
        }
        setNoOfCases(nextNoOfCases);
      }
    } else {
      const cartItem = cartItems.find((e) => e.cart_id === cart_id);
      if (cartItem) {
        const { cases } = cartItem;

        if (
          (value === true && cartItem.dye_type === DYED_TYPES.piece) ||
          (value === false && cartItem.dye_type === DYED_TYPES.top)
        ) {
          const idx = filtered.findIndex(
            (e) => String(e?.shade?.descr) === String(cartItem.shade_name),
          );
          if (idx >= 0) {
            setSelectedShadeIndex(idx);
          }
          setNoOfCases(cases);
        } else {
          setSelectedShadeIndex(0);
          let nextNoOfCases = value ? 2 : 1; // PD->2, TD->1
          if (filtered.length === 0) {
            nextNoOfCases = 0;
          }
          setNoOfCases(nextNoOfCases);
        }
      }
    }
  };

  const onShadeSelected = (idx) => {
    setSelectedShadeIndex(idx);
    setShadesModal(false);
  };

  const minusNoOfCases = () => {
    let next = noOfCases - (isPDSelected ? 2 : 1);
    next = clampAndNormalizeCases(next);
    setNoOfCases(next);
  };

  const plusNoOfCases = () => {
    let next = noOfCases + (isPDSelected ? 2 : 1);
    next = clampAndNormalizeCases(next);
    setNoOfCases(next);
  };

  const availableCases = useMemo(() => {
    const shade = filteredItemsList?.[selectedShadeIndex];
    const cases = shade?.bsr_stock_cases || [];

    const occupiedCaseIds = new Set();
    cartItems.forEach((item) => {
      if (cart_id && String(item.cart_id) === String(cart_id)) return;

      String(item?.cases_no || "")
        .split(",")
        .map((s) => s?.trim())
        .filter(Boolean)
        .forEach((id) => occupiedCaseIds.add(id));
    });

    return cases.filter(
      (stockCase) => !occupiedCaseIds.has(String(stockCase.caseno)),
    );
  }, [filteredItemsList, selectedShadeIndex, cartItems]);

  // Recalculate totals when qty or stock list changes
  useEffect(() => {
    calculateTotalMtrs();
  }, [noOfCases, availableCases]);

  const calculateTotalMtrs = () => {
    let totalMtrs = 0;
    let slabMtrs = 250; // default slab
    let inStockQty = 0;
    let tempPossibleInStockQuantity = 0;
    const tempNoOfCases = clampAndNormalizeCases(noOfCases);

    if (availableCases.length > 0) {
      if (tempNoOfCases > 0) {
        const taken = availableCases.slice(0, tempNoOfCases);
        inStockQty = taken.reduce(
          (sum, item) => sum + toNumber(item?.stockqty, 0),
          0,
        );
        totalMtrs = inStockQty;

        tempPossibleInStockQuantity = availableCases.reduce(
          (sum, item) => sum + toNumber(item?.stockqty, 0),
          0,
        );
      }
      if (tempNoOfCases > availableCases.length) {
        totalMtrs =
          totalMtrs + (tempNoOfCases - availableCases.length) * slabMtrs;
      }
    } else {
      totalMtrs = totalMtrs + tempNoOfCases * slabMtrs;
    }
    // Round to 1 decimal place
    const rounded = Math.round(totalMtrs * 10) / 10;
    const roundedInStock = Math.round(inStockQty * 10) / 10;

    setPossibleInStockQuantity(tempPossibleInStockQuantity);
    setTotalInStockQuantity(roundedInStock);
    setTotalQuantity(rounded);

    return { tempPossibleInStockQuantity, inStockQty: roundedInStock, rounded };
  };

  const isInStock = useMemo(() => {
    const dcl = toNumber(productData?.quality_shade?.dcl_stockmtrs, 0);
    return possibleInStockQuantity > dcl;
  }, [
    possibleInStockQuantity,
    filteredItemsList,
    selectedShadeIndex,
    productData,
  ]);

  const handleAddToCart = async (e, approved = false) => {
    try {
      if (!productData?.product?.product_id) {
        return showPopup({
          title: null,
          content: (
            <span className="custom-modal-title">Add product image first!</span>
          ),
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
      }
      if (
        !productData ||
        filteredItemsList.length === 0 ||
        !Number(totalQuantity)
      )
        return;

      await new Promise((resolve) => setTimeout(resolve, 300));

      const shade = filteredItemsList[selectedShadeIndex];
      const tempNoOfCases = clampAndNormalizeCases(noOfCases);
      const { rounded: tempTotalQuantity, inStockQty: tempInStockQty } =
        calculateTotalMtrs();
      const selectedCases = availableCases.slice(0, tempNoOfCases);
      const casesInStock = Math.min(tempNoOfCases, availableCases.length);
      const makeToOrder = Math.max(0, tempNoOfCases - availableCases.length);

      if (makeToOrder > 0 && !approved && casesInStock > 0 && makeToOrder > 0) {
        setModalData({
          shade,
          selectedCases,
          casesInStock,
          makeToOrder,
        });
        setOrderSplitModal(true);
        return;
      }

      const selectedShadeNo = String(
        shade?.descr_shade || shade?.shade_no || "",
      );

      const payload = {
        sort_no,
        basic_sortno: productData?.basic_sortno,
        fabric_type: productData?.fabric_type,
        user_id: is_customer ? 0 : user_id,
        cart_id,
        product_id: productData?.product?.product_id,
        quality_code: String(productData?.quality_code || ""),
        clienttype: productData?.clienttype,
        short_name: productData?.short_name || "-",
        product_picture:
          shade?.shade?.detail?.shade_picture_url?.split("/")?.pop() || "",
        price_per_meter: productData?.rate,
        fabric_specs: `${productData?.finish_width || ""}, ${productData?.finish_wt || ""}, ${
          productData?.blend_name || ""
        }`,
        dye_type: shade?.dyed_type || "-",
        shade_no: selectedShadeNo,
        shade_name: String(shade?.descr_shade || ""),
        cases_id: selectedCases.map((e) => e.stock_id).join(","),
        cases_no: selectedCases.map((e) => e.caseno).join(","),
        cases: String(tempNoOfCases),
        cases_in_stock: String(casesInStock),
        cases_make_to_order: String(makeToOrder),
        total_quantity: tempTotalQuantity,
        total_in_stock_quantity: Number(casesInStock) < 1 ? 0 : tempInStockQty,
        total_make_to_order_quantity:
          Number(makeToOrder) < 1
            ? 0
            : Math.round((tempTotalQuantity - tempInStockQty) * 10) / 10,
        comments: comments?.trim(),
        tdlevel_code: productData.tdlevel_code,
        tdlevel_name: productData.tdlevel_name,
        customer_id: is_customer ? customer_id : undefined,
      };
      const addUrl = is_customer
        ? "cart/add-to-cart"
        : "sales/cart/add-to-cart";
      const updateUrl = is_customer
        ? "cart/update-cart"
        : "sales/cart/update-cart";

      const res = await postApi(
        cart_id ? updateUrl : addUrl,
        payload,
        true,
        null,
        () => {
          if (cart_id) {
            navigate(handleRouteNav("/product-catalog/checkout", role_id));
          }
        },
      );
      if (res?.status) {
        setComments("");
        setNoOfCases(isPDSelected ? 2 : 1);
        fetchCart();
        getShadeStockCases();
        setOrderSplitModal(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpload = async (files) => {
    if (!files?.length) {
      showPopup({
        title: null,
        content: (
          <span className="custom-modal-title">
            Please select an image file to upload.
          </span>
        ),
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }
    const file = files[0]?.originFileObj || files[0];
    if (file.type === "image/jpg") file.type = "image/jpeg";

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      showPopup({
        title: null,
        content: (
          <span className="custom-modal-title">
            Please upload only JPG or PNG files
          </span>
        ),
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }

    const formData = new FormData();
    formData.append("quality_code", String(currentItem?.quality_code || ""));
    formData.append("sort_no", String(currentItem?.sort_no || ""));
    formData.append("updated_by", String(full_name || ""));
    formData.append("updated_by_id", String(user_id || ""));
    formData.append("product_pic", file);
    try {
      const response = await postApi("sales/product/update", formData, true);

      console.log("Upload successful", response.data);
      setIsModalOpen(false);
      fetchProductData();
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
    }
  };

  const shadeChips = (sliced = true) => {
    const visibleShades = 4;
    const selectedShade = filteredItemsList[selectedShadeIndex];
    const rearrangedList =
      selectedShadeIndex > visibleShades - 1 && sliced
        ? [
            selectedShade,
            ...filteredItemsList.filter(
              (_, index) => index !== selectedShadeIndex,
            ),
          ]
        : filteredItemsList;
    const finalShades = sliced
      ? rearrangedList.slice(0, visibleShades)
      : rearrangedList;
    const showMore = sliced && rearrangedList.length > visibleShades;

    return (
      <>
        <div
          className={`flex flex-wrap gap-3 justify-center ${sliced ? "" : ""}`}
        >
          {finalShades.length > 0 ? (
            <>
              {finalShades?.map((shade, idx) => {
                const isSelected = shade === selectedShade;
                return (
                  <div
                    key={idx}
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() =>
                      onShadeSelected(filteredItemsList.indexOf(shade))
                    }
                  >
                    <div
                      className={`w-16 h-16 rounded-xl relative overflow-hidden flex items-center justify-center transition-all duration-300 transform hover:scale-105 ${
                        isSelected
                          ? "ring-2 ring-red-700 ring-offset-2 scale-105 shadow-lg"
                          : "shadow-md hover:shadow-lg"
                      }`}
                    >
                      {shade?.shade?.detail?.shade_picture_url ? (
                        <img
                          src={shade?.shade.detail?.shade_picture_url}
                          alt={shade?.descr_shade}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <span className="text-gray-600 text-xs font-medium">
                          {shade?.descr_shade}
                        </span>
                      )}
                    </div>

                    <span className="mt-1 text-sm text-gray-700">
                      {shade?.descr_shade}
                    </span>
                  </div>
                );
              })}
              {showMore && filteredItemsList.length > visibleShades && (
                <button
                  onClick={() => setShadesModal(true)}
                  className={`w-16 h-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 border border-gray-300`}
                >
                  <span className="text-gray-600 font-semibold text-sm">
                    +{filteredItemsList.length - visibleShades}
                  </span>
                </button>
              )}
            </>
          ) : (
            <span className="text-black text-xs">
              Sorry! We couldn't find any matches.
            </span>
          )}
        </div>
      </>
    );
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Product Details");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "Product Details",
              href: handleRouteNav(`/product-details/${sort_no}`, role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  const toggleFav = (e) => {
    setProductData((prev) => {
      const isFav = prev?.product?.wishlist_tag === "yes";
      if (isFav) {
        rmoveWishlistItem(prev);
      } else {
        addWishlistItem(prev);
      }

      return {
        ...prev,
        product: {
          ...prev.product,
          wishlist_tag: isFav ? "no" : "yes",
        },
      };
    });
  };

  const addWishlistItem = async (item) => {
    const payload = {
      customer_id: customer_id,
      product_id: item?.product?.product_id,
      sort_no: item?.product?.sort_no,
    };
    const res = await postApi("wishlist/add-wishlist", payload);
  };

  const rmoveWishlistItem = async (item) => {
    const payload = {
      customer_id: customer_id,
      product_id: item?.product?.product_id,
      sort_no: item?.product?.sort_no,
    };
    const res = await postApi("wishlist/delete-wishlist-product", payload);
  };

  return (
    <>
      <>
        <div className="product-details-container form-css max-w-[1200px] mx-auto p-6 overflow-x-hidden">
          <Row gutter={24} className="flex flex-wrap md:flex-nowrap">
            {/* LEFT SECTION - IMAGE FIRST, THEN TITLE AND PRICE */}
            <Col xs={24} md={12} className="flex flex-col">
              {/* Product Image */}
              <Card
                className="w-full mb-6 rounded-[24px] overflow-hidden relative"
                cover={
                  // <img
                  //   alt={productData?.short_name}
                  //   src={productData?.product?.product_pic_url || "/fallback-image.png"}
                  //   className="h-[350px] object-cover border-b border-gray-200"
                  // />
                  <ProductImageName
                    img_url={productData?.product?.product_pic_url}
                    img_alt={productData?.short_name}
                    parent_classname="h-[350px] object-cover border-b border-gray-200"
                    img_classname="w-full h-full object-cover rounded-xl"
                  />
                }
                bodyStyle={{ padding: 0 }}
              >
                {!is_customer && (
                  <div className="absolute top-4 left-4">
                    <Dropdown
                      menu={DropdownMenuItems({
                        item: productData,
                        fetchData: fetchProductData,
                        setCurrentItem,
                        setIsModalOpen,
                        navigate,
                        postApi,
                        role_id,
                        full_name,
                        user_id,
                        showPopup,
                      })}
                      trigger={["click"]}
                      overlayClassName="custom-dropdown-sales-menu"
                    >
                      <Typography.Link
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div
                          className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center"
                          shape="circle"
                          size="middle"
                        >
                          <TbDotsVertical className="text-red-700" size={18} />
                        </div>
                      </Typography.Link>
                    </Dropdown>
                  </div>
                )}

                {is_customer ? (
                  <div
                    className="absolute top-4 right-4 p-0 border border-transparent hover:border-[#A33434] transition-all duration-200"
                    style={{
                      position: "absolute",
                      width: 50,
                      height: 50,
                      borderRadius: "10px",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    }}
                    onClick={(e) => {
                      toggleFav(productData);
                    }}
                  >
                    {productData?.product?.wishlist_tag == "yes" ? (
                      <svg
                        className="cursor-pointer"
                        width="26"
                        height="22"
                        viewBox="0 0 21 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21 5.8125C21 12.375 11.2697 17.6869 10.8553 17.9062C10.7461 17.965 10.624 17.9958 10.5 17.9958C10.376 17.9958 10.2539 17.965 10.1447 17.9062C9.73031 17.6869 0 12.375 0 5.8125C0.00173694 4.27146 0.614681 2.79404 1.70436 1.70436C2.79404 0.614681 4.27146 0.00173694 5.8125 0C7.74844 0 9.44344 0.8325 10.5 2.23969C11.5566 0.8325 13.2516 0 15.1875 0C16.7285 0.00173694 18.206 0.614681 19.2956 1.70436C20.3853 2.79404 20.9983 4.27146 21 5.8125Z"
                          fill="#A33434"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="cursor-pointer"
                        width="21"
                        height="18"
                        viewBox="0 0 21 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          opacity="0.1"
                          d="M21 5.8125C21 12.375 11.2697 17.6869 10.8553 17.9062C10.7461 17.965 10.624 17.9958 10.5 17.9958C10.376 17.9958 10.2539 17.965 10.1447 17.9062C9.73031 17.6869 0 12.375 0 5.8125C0.00173694 4.27146 0.614681 2.79404 1.70436 1.70436C2.79404 0.614681 4.27146 0.00173694 5.8125 0C7.74844 0 9.44344 0.8325 10.5 2.23969C11.5566 0.8325 13.2516 0 15.1875 0C16.7285 0.00173694 18.206 0.614681 19.2956 1.70436C20.3853 2.79404 20.9983 4.27146 21 5.8125Z"
                          fill="black"
                        />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div className="absolute top-4 right-4 bg-opacity-90 rounded-full">
                    <Button
                      className="rounded-full text-2xl h-[40px] w-24"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          handleRouteNav("/product-catalog/favorites", role_id),
                          {
                            state: {
                              sort_no: productData?.sort_no,
                              short_name: productData?.short_name,
                            },
                          },
                        );
                      }}
                    >
                      <FaHeart className="text-[#A33434] text-2xl" />{" "}
                      {productData?.product?.wishlist_count || 0}
                    </Button>
                  </div>
                )}
              </Card>

              <div className="flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start font-abhaya gap-2 sm:gap-4">
                  {/* Title */}
                  <Title
                    level={2}
                    className="!mb-0 !text-left font-abhaya !font-normal truncate w-full sm:w-auto"
                    style={{
                      fontSize: "clamp(24px, 5vw, 40px)",
                      lineHeight: "90%",
                    }}
                    title={productData?.short_name}
                  >
                    {productData?.short_name}
                  </Title>

                  {/* Badge */}
                  <div className="flex mt-2 sm:mt-0 items-start">
                    {filteredItemsList.length > 0 &&
                      (isInStock ? (
                        <CustomBadge variant="success" text="In Stock" />
                      ) : (
                        <CustomBadge
                          variant="pending"
                          text={
                            <span className="text-nowrap whitespace-nowrap">
                              Make to order
                            </span>
                          }
                        />
                      ))}
                  </div>
                </div>

                <div className="flex flex-col">
                  <Text className="!text-left font-manrope !font-medium  !text-[24px]  !leading-[120%] !text-[#484848]">
                    {productData?.clienttype} {productData.tdlevel_name}
                  </Text>
                  <div className="!text-left flex items-baseline">
                    {/* <Title
                      level={3}
                      className="!mb-0 !inline-block !mr-1  !text-[35px]  !font-medium !font-manrope"
                    >
                      ₹{productData?.rate} / meter
                    </Title> */}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap xl:flex-nowrap gap-4 mt-8 w-full">
                {/* Add to Cart Section */}
                {filteredItemsList.length > 0 && (
                  <PrimaryButton
                    className="flex-1"
                    text={cart_id ? "Update to cart" : "Add to cart"}
                    onClick={handleAddToCart}
                    icon={
                      cart_id ? (
                        <ReloadOutlined className="w-4 h-4 text-white" />
                      ) : (
                        <PlusOutlined className="w-4 h-4 text-white" />
                      )
                    }
                    iconOnleft
                    loading={loading}
                    disabled={Number(noOfCases) < 1}
                  />
                )}

                {/* Proceed to Checkout Button */}
                {!cart_id && (
                  <PrimaryButton
                    text={"Proceed to cart"}
                    onClick={handleCheckout}
                    noIcon
                    className="flex-1"
                    btnType="secondary"
                  />
                )}
              </div>
              <div className="max-w-2xl mx-auto pt-6 rounded-lg  mt-8">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">
                    Disclaimers
                  </h2>
                </div>

                <div className="space-y-4">
                  {disclaimerItems.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="w-2 h-2 bg-[#A33434] rounded-full"></div>
                      </div>
                      <p className="flex-1 text-left text-gray-600 leading-relaxed text-sm">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            {/* VERTICAL DIVIDER */}
            <Divider
              type="vertical"
              className="mx-6 border-l-2 border-gray-300 h-auto"
            />

            {/* RIGHT SECTION - PRODUCT DETAILS */}
            <Col xs={24} md={12} className="flex flex-col gap-6 !pr-12">
              {/* Fabric Specification */}
              <div>
                <Title
                  level={5}
                  className="!text-left !text-gray-800 !text-[35px] font-abhaya !mb-0"
                >
                  Fabric Specifications
                </Title>
                <div className="grid grid-cols-1 col-wi">
                  <div
                    className={`${is_customer ? " md:w-[88%]" : !collapsed ? "md:w-[88%]" : ""} flex flex-col gap-y-2 text-base`}
                  >
                    <div className="flex justify-between">
                      <Text className="text-gray-500">Width:</Text>
                      <Text className="font-medium text-gray-900">
                        {Number(productData.finish_width) || 0}" inch /{" "}
                        {inchesToCm(productData.finish_width)} cm
                      </Text>
                    </div>
                    <div className="flex justify-between">
                      <Text className="text-gray-500">Weight:</Text>
                      <Text className="font-medium text-gray-900">
                        {productData.finish_wt || 0} GLM
                      </Text>
                    </div>
                    <div className="flex justify-between">
                      <Text className="text-gray-500">Blend:</Text>
                      <Text className="font-medium text-gray-900">
                        {productData.blend_name}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dye Type Section */}
              <div>
                <Text className="text-sm text-gray-500 block text-left mb-3  text-[20px]">
                  Dyed type
                </Text>
                <div className="flex gap-3 w-full">
                  {/* flex container replaces Space */}
                  <Card
                    hoverable={pdItemsList.length > 0}
                    onClick={() => onPdTdTap(true)}
                    className={`w-[183px] h-[70px] transition-all duration-200 rounded-[16px] ${
                      isPDSelected
                        ? "!border-[2px] !border-[#A33434] bg-red-50 text-red-700 pointer-events-none"
                        : "border-gray-200"
                    }  ${
                      pdItemsList.length === 0
                        ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none"
                        : ""
                    }`}
                  >
                    <Text className="text-sm text-gray-500">
                      Piece Dyed (PD)
                    </Text>
                  </Card>
                  <Card
                    hoverable={tdItemsList.length > 0}
                    onClick={() => onPdTdTap(false)}
                    className={`w-[183px] h-[70px] transition-all duration-200 rounded-[16px]  ${
                      !isPDSelected
                        ? "!border-[2px] !border-[#A33434] bg-red-50 text-red-700 pointer-events-none"
                        : "border-gray-200"
                    } ${
                      tdItemsList.length === 0
                        ? "opacity-50 cursor-not-allowed hover:!cursor-not-allowed hover:shadow-none"
                        : ""
                    }`}
                  >
                    <Text className="text-sm text-gray-500">Top Dyed (TD)</Text>
                  </Card>
                </div>
              </div>
              <div>
                <Text className="text-sm text-gray-500 block mb-4 text-left text-[20px]">
                  Shade
                </Text>
                <div className="w-full flex flex-wrap gap-3">
                  {shadeChips()}
                </div>
              </div>

              {/* Quantity Section */}
              {filteredItemsList.length > 0 && (
                <>
                  <div>
                    <Text className="text-sm text-gray-500 block mb-3 text-left text-[20px]">
                      Quantity
                    </Text>
                    <div
                      className={`${is_customer ? " md:w-[88%]" : !collapsed ? "md:w-[88%]" : ""} flex items-center justify-between gap-2`}
                    >
                      <div className="flex items-center justify-between gap-x-2">
                        {/* <div className="flex items-center gap-x-2"> */}
                        {/* Minus Button */}
                        <Button
                          shape="circle"
                          onClick={minusNoOfCases}
                          disabled={noOfCases <= (isPDSelected ? 2 : 1)}
                          icon={<MinusOutlined className="!text-2xl" />}
                          className={`!w-[50px] !h-[50px] !min-w-[50px] flex items-center justify-center !text-2xl ${
                            noOfCases <= (isPDSelected ? 2 : 1)
                              ? "!border-gray-200 !text-gray-300 !cursor-not-allowed"
                              : "!border-gray-300 !text-gray-600 hover:!text-[#A33434] hover:!border-[#A33434]"
                          }`}
                        />

                        <div className="bg-white h-[50px] w-[150px] rounded-full border border-gray-200 flex items-center justify-center shadow-sm px-3">
                          <div className="flex items-center justify-center space-x-1">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={noOfCases}
                              onBlur={(e) => {
                                const value = e.target.value;
                                setNoOfCases(clampAndNormalizeCases(value));
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                // allow empty string while typing
                                if (raw === "") {
                                  setNoOfCases("");
                                  return;
                                }
                                // allow only digits
                                if (!/^\d+$/.test(raw)) return;
                                setNoOfCases(raw);
                              }}
                              className="bg-transparent outline-none text-gray-700 font-medium text-[18px] text-center w-[40px] no-spinner"
                            />
                            <span className="text-gray-500 font-medium">
                              cases
                            </span>
                          </div>
                        </div>

                        {/* Plus Button */}
                        <Button
                          shape="circle"
                          onClick={plusNoOfCases}
                          icon={<PlusOutlined className="!text-2xl" />} // keep icon static
                          className="!w-[50px] !h-[50px] !min-w-[50px] flex items-center justify-center
                                  !border-gray-300 !text-gray-600 !text-2xl
                                  hover:!text-[#A33434] hover:!border-[#A33434]"
                        />
                      </div>

                      <div className="flex justify-end">
                        <div className="bg-gray-200 flex items-center justify-center opacity-100 p-3 rounded-[12px]">
                          <span className="whitespace-nowrap font-manrope font-bold text-[18px] leading-[110%] text-center text-[#A33434]">
                            {totalQuantity.toLocaleString()} m
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Truck icon inside background circle */}
                    <div className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-full flex items-center justify-center">
                      <img
                        src="/assets/icons/TruckTrailer.png"
                        alt="Truck"
                        className="w-4 h-4 object-contain"
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <Text className="text-left text-gray-700">
                        <strong>Estimated Dispatch</strong> - Piece-dyed 30-40
                        days, Top-dyed 45-60 days.
                      </Text>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div className="flex flex-col">
                    <Text className="text-sm text-gray-500 block mb-3 text-left">
                      Comments
                    </Text>
                    <TextArea
                      maxLength={100}
                      value={comments}
                      onChange={(e) => {
                        const cleanValue = e.target.value.replace(
                          /[\n\r\t]/g,
                          "",
                        );
                        setComments(cleanValue);
                      }}
                      placeholder="Write any additional notes here..."
                      rows={4}
                      classNames="w-full border border-gray-300 rounded-lg placeholder-gray-400 text-gray-700 shadow-sm bg-transparent !hover:border-gray-300 !focus:border-gray-300 !focus:shadow-none !active:border-gray-300 !outline-none"
                      style={{
                        boxShadow: "none",
                      }}
                    />
                  </div>
                </>
              )}
            </Col>
          </Row>
        </div>
      </>

      <Modal
        className="flex flex-col items-center justify-center form-css-5 full-width-modal"
        open={orderSplitModal}
        footer={null}
        onCancel={() => setOrderSplitModal(false)}
        width={450}
      >
        <Title className="input-css font-abhaya !mb-0" level={2}>
          Important Information!
        </Title>
        <Title className=" !text-gray-500 !font-normal" level={5}>
          {is_customer ? "Your" : "This"} order will be divided into two parts.
        </Title>

        <div className="flex gap-3 justify-center items-center mt-4">
          <div className="w-[40px] h-[40px] bg-[#A334341A] rounded-full flex items-center justify-center shrink-0">
            <img
              src="/assets/icons/TruckTrailer.png"
              alt="Cube"
              className="w-auto h-[20px] object-contain"
            />
          </div>

          <div className="flex flex-col text-start">
            <span className="">
              {is_customer
                ? "For dispatch information please contact your sales representative."
                : "For dispatch information please inform clients to contact their sales representative."}
            </span>
          </div>
        </div>

        <div className="w-full rounded-2xl mt-5 border p-3">
          <div className="flex items-center gap-4">
            <div className="w-[50px] h-[50px] flex justify-center items-center">
              <img
                src={modalData?.shade?.shade?.detail?.shade_picture_url}
                alt={productData.short_name}
                className="w-[50px] h-[50px]"
              />
            </div>
            <div className="flex flex-col justify-start text-start">
              <p className="text-xl font-bold break-words whitespace-normal">
                {productData.short_name}
              </p>
              <p className="font-semibold text-gray-500 text-lg">
                {/* ₹{Number(productData.rate || 0).toLocaleString("en-IN")} / meter */}
                {modalData?.shade?.descr_shade}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 font-medium leading-6 gap-4 my-3">
            <div className="text-left leading-6 space-y-1">
              <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                In Stock
                <span className="block mr-1 h-1.5 w-1.5 rounded-full bg-[#009F10] ring-2 ring-white"></span>
              </div>
              <p className="text-base leading-6 font-medium text-gray-900">
                {modalData.casesInStock || 0} cases (
                {totalInStockQuantity.toFixed(2) || 0} m)
              </p>
            </div>
            <div className={`text-right leading-6 space-y-1`}>
              <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                Make to order
                <span className="block h-1.5 w-1.5 rounded-full bg-[#FFB32E] ring-2 ring-white"></span>
              </div>
              <p className="text-base leading-6 font-medium text-gray-900">
                {modalData.makeToOrder || 0} cases (
                {(totalQuantity - totalInStockQuantity).toFixed(2) || 0} m)
              </p>
            </div>
          </div>
        </div>

        <div className="flex mt-8 w-full justify-center">
          <PrimaryButton
            text={cart_id ? "Update to cart" : "Add to cart"}
            disabled={Number(noOfCases) < 1}
            icon={
              cart_id ? (
                <ReloadOutlined className="w-4 h-4 text-white" />
              ) : (
                <PlusOutlined className="w-4 h-4 text-white" />
              )
            }
            onClick={(e) => handleAddToCart(e, true)}
            iconOnleft
            loading={loading}
          />
        </div>
      </Modal>

      <Modal
        className="flex flex-col items-center justify-center form-css-5 w-[500px]"
        open={shadesModal}
        footer={null}
        onCancel={() => setShadesModal(false)}
        width={450}
      >
        <Title className="font-abhaya !mb-8" level={2}>
          {isPDSelected
            ? `${DYED_TYPES.pieceLabel} (PD) Shades`
            : `${DYED_TYPES.topLabel} (TD) Shades`}
        </Title>
        {shadeChips(false)}
      </Modal>

      <FileUploadModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onUpload={handleUpload} // Using the function here
        title="Upload Image"
        fileTypes={[".jpg", ".jpeg", ".png"]}
        buttonText="Update Image"
        fileTypeLabel="JPG or PNG"
      />
    </>
  );
};

export default ProductDetails;
